import { catchError, map, takeUntil } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom, Observable, Subject, timer } from "rxjs";
import { Auth } from "../auth";
import { DefaultRequestOptions } from "./default-request-options";
import { UI as UIService } from "../../services/ui.service";
import { environment } from "app/environments/environment";
import { HttpRequestOptions } from "../../domain/http-request-options";
import { ToastrService } from "ngx-toastr";

const TAG = "HttpService:";

@Injectable()
export class HttpService {
  endpoint: string;
  private rateLimitToastDebounceTimer: any = null;

  constructor(
    public http: HttpClient,
    private auth: Auth,
    private UI: UIService,
    private _toastr: ToastrService,
  ) {
    this.endpoint = environment.ENDPOINT + "/api/";
  }

  handleResponse(responseObservable: Observable<any>): Promise<any> {
    return firstValueFrom(
      responseObservable.pipe(
        map((response) => {
          if (response.body["success"]) {
            return response.body["message"];
          }
          const error = new Error(response.body["message"] || "general error");
          (error as any).status = response.status;
          throw error;
        }),
        catchError((error) => {
          if (error.status === 401) {
            if (environment.production) {
              this.auth.remove();
              window.location.href = "/login";
            }
          }
          if (error.status === 429) {
            // Extract Retry-After header if available
            let retryAfterMessage = "Please try again later.";

            // Try different ways to access the Retry-After header
            let retryAfterValue = null;

            // Method 1: Direct headers access (most common with Angular HttpClient)
            if (error.headers && error.headers.get("Retry-After")) {
              retryAfterValue = error.headers.get("Retry-After");
            }
            // Method 2: Error response headers (lowercase)
            else if (
              error.error &&
              error.error.headers &&
              error.error.headers["retry-after"]
            ) {
              retryAfterValue = error.error.headers["retry-after"];
            }
            // Method 3: Response object headers (proper case)
            else if (
              error.error &&
              error.error.headers &&
              error.error.headers["Retry-After"]
            ) {
              retryAfterValue = error.error.headers["Retry-After"];
            }
            // Method 4: Check if error itself has the header
            else if (error.error && error.error["Retry-After"]) {
              retryAfterValue = error.error["Retry-After"];
            }
            // Method 5: Check response property
            else if (
              error.response &&
              error.response.headers &&
              error.response.headers.get("Retry-After")
            ) {
              retryAfterValue = error.response.headers.get("Retry-After");
            }

            if (retryAfterValue) {
              const retryAfterSeconds = parseInt(retryAfterValue, 10);
              if (!isNaN(retryAfterSeconds)) {
                if (retryAfterSeconds < 60) {
                  retryAfterMessage = `Please try again in ${retryAfterSeconds} seconds.`;
                } else if (retryAfterSeconds < 3600) {
                  const minutes = Math.ceil(retryAfterSeconds / 60);
                  retryAfterMessage = `Please try again in ${minutes} minute${minutes > 1 ? "s" : ""}.`;
                } else {
                  const hours = Math.ceil(retryAfterSeconds / 3600);
                  retryAfterMessage = `Please try again in ${hours} hour${hours > 1 ? "s" : ""}.`;
                }
              }
            }
            this.showRateLimitToast(retryAfterMessage);
          }

          throw error;
        }),
      ),
    );
  }

  /**
   * Parsing JSON and return as-is (Unicode)
   * @return Promise
   */
  parseJSONResponse(responseObservable: Observable<any>): Promise<any> {
    return firstValueFrom(responseObservable).then((response) => response.body);
  }

  /**
   * Is Relative URL
   * @return boolean
   */
  isRelative(url) {
    return !new RegExp(/^http[s]?:\/\//).test(url);
  }

  /**
   * Prepare URL before calling an endpoint
   * @return string
   */
  prepareUrl(url) {
    return this.isRelative(url) ? this.endpoint + url : url;
  }

  /**
   * Download JSON files from a URL
   */
  getJSON(url: string, options?: HttpRequestOptions): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };
    return this.parseJSONResponse(this.http.get(this.prepareUrl(url), options));
  }

  getCodecJson(url: string, options?: HttpRequestOptions): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };
    return this.parseJSONResponse(this.http.get(url, options));
  }

  get(
    url: string,
    options?: HttpRequestOptions,
    abortSubject?: Subject<void>,
  ): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };

    let abortReference = new Subject<void>();
    if (abortSubject) {
      abortReference = abortSubject;
    }
    return this.handleResponse(
      this.http
        .get(this.prepareUrl(url), options)
        .pipe(takeUntil(abortReference)),
    );
  }

  getFile(url: string, filename: string): Promise<any> {
    const defaultRequestOptions = this.createDefaultRequestOptions();
    defaultRequestOptions.responseType = 'blob' as 'json'; // <-- FIX
    (defaultRequestOptions as any).observe =  'response';

    return this.http
      .get(this.prepareUrl(url), defaultRequestOptions)
      .pipe(
        map((response) => {
          const fileUrl = window.URL.createObjectURL(response["body"] as Blob);
          const a = document.createElement("a");
          document.body.appendChild(a);
          a.href = fileUrl;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(fileUrl);
        }),
      )
      .toPromise();
  }
  getMediaFile(url: string, filename: string): Promise<any> {
    return this.handleResponse(
      this.http.get(this.prepareUrl(url), this.createDefaultRequestOptions()),
    )
      .then((response) => {
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.href = response?.body || response;
        a.target = "_blank";
        a.type = "application/octet-stream";
        a.download = filename;
        a.click();
      })
      .catch((res) => {
        let title;
        let message;
        if (res.status === 400) {
          title = "File is archived";
          if (res.error && res.error.message) {
            message = res.error.message;
          }
        }
        this.UI.notify(this.UI.EVENTS.ShowAlert, {
          title: title || "ERROR",
          message: message || `File was not downloaded.<br>Please try again.`,
        });
      });
  }

  post(url: string, body: any, options?: HttpRequestOptions): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };
    return this.handleResponse(
      this.http.post(this.prepareUrl(url), body, options),
    );
  }

  put(url: string, body: any, options?: HttpRequestOptions): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };
    return this.handleResponse(
      this.http.put(this.prepareUrl(url), body, options),
    );
  }

  patch(url: string, body: any, options?: HttpRequestOptions): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };
    return this.handleResponse(
      this.http.patch(this.prepareUrl(url), body, options),
    );
  }

  delete(url: string, options?: HttpRequestOptions): Promise<any> {
    options = { ...this.createDefaultRequestOptions(), ...options };
    return this.handleResponse(this.http.delete(this.prepareUrl(url), options));
  }

  createDefaultRequestOptions() {
    const defaultRequestOptions = new DefaultRequestOptions(this.auth);
    (defaultRequestOptions as any).observe = "response";
    return defaultRequestOptions;
  }

  createDefaultNonAuthRequestOption() {
    return new DefaultRequestOptions();
  }

  /**
   * Shows a debounced rate limit toast to prevent multiple toasts from appearing
   * when multiple API calls hit the rate limit simultaneously
   */
  private showRateLimitToast(message: string): void {
    // Clear any existing timer
    if (this.rateLimitToastDebounceTimer) {
      clearTimeout(this.rateLimitToastDebounceTimer);
    }

    // Set a new timer to show the toast after 500ms
    this.rateLimitToastDebounceTimer = setTimeout(() => {
      this._toastr.error(message, "Rate limit exceeded");
      this.rateLimitToastDebounceTimer = null;
    }, 1000);
  }
}
